import React from 'react'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {GetTheAppButtons} from 'components/GetTheAppButtons/GetTheAppButtons'
import {getLanguageCode} from 'modules/meta/selectors'
import Image from 'next/image'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {PageWidthContainer} from '@vio/atlas/helpers/PageWidthContainer'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {Placement} from '@vio/core/src/deepLinking/types'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import {HomePageSectionTitle} from '../commonStyles'
import {HighlightedTitle, HighlightedTitleAccent} from '../HighlightedTitle'

import type {Brand} from '@vio/core/src/brand/types'

const SectionWrapper = styled.div`
  margin: 0 ${cssTheme.layout.spacing.s500};
  border-radius: ${cssTheme.layout.radius.xxl};
  background: ${cssTheme.colors.background.neutral.secondary};

  ${linariaMq.desktopSm} {
    margin: 0;
  }
`

const ComparisonWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: ${cssTheme.layout.spacing.s700};

  & > div {
    flex-grow: 1;
    padding: 0 ${cssTheme.layout.spacing.s500};
  }

  ${linariaMq.desktopSm} {
    justify-content: center;
    flex-direction: row;
    padding-top: ${cssTheme.layout.spacing.s600};
  }
`

const ComparisonItemTitle = styled(Text)`
  margin-bottom: ${cssTheme.layout.spacing.s250};
`

const GetAppWrapper = styled.div`
  border-bottom-left-radius: ${cssTheme.layout.radius.xxl};
  border-bottom-right-radius: ${cssTheme.layout.radius.xxl};
  background: ${cssTheme.colors.background.brand.secondary};
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: ${cssTheme.layout.spacing.s600};
  padding: ${cssTheme.layout.spacing.s700} ${cssTheme.layout.spacing.s600};

  button {
    min-width: fit-content;
  }

  ${linariaMq.desktopXs} {
    padding: ${cssTheme.layout.spacing.s900};
    justify-content: space-between;
    flex-direction: row;
  }
`

const getValueProps = (messages: Brand['messages']) => [
  {
    title: messages.homeValuePropsExclusiveDealsTitleMessage,
    subtitle: messages.homeValuePropsExclusiveDealsSubtitleMessage,
    iconUrl: '/static/saving-section/search.svg',
    iconName: 'FindBaggage'
  },
  {
    title: messages.homeValuePropsCompareSitesTitleMessage,
    subtitle: messages.homeValuePropsCompareSitesSubtitleMessage,
    iconUrl: '/static/saving-section/compare.svg',
    iconName: 'Medal'
  },
  {
    title: messages.homeValuePropsBookTheBestTitleMessage,
    subtitle: messages.homeValuePropsBookTheBestSubtitleMessage,
    iconUrl: '/static/saving-section/best.svg',
    iconName: 'Podium'
  }
]

export const SavingSection = () => {
  const {isMobile} = useDeviceLayout()
  const {formatMessage} = useIntl()
  const languageCode = useSelector(getLanguageCode)
  const {brand} = useBrand()
  const {
    shell: {hasHomeSavingsCounter},
    retention: {hasGetTheApp}
  } = useBrandCapabilities()
  const valueProps = getValueProps(brand.messages)

  return (
    <PageWidthContainer>
      <SectionWrapper>
        <Box
          paddingTop="s700"
          paddingBottom="s900"
          paddingLeft="s600"
          paddingRight="s600"
          desktopXs={{
            paddingTop: 's950',
            paddingBottom: 's950',
            paddingLeft: 's900',
            paddingRight: 's900'
          }}
        >
          <Box paddingBottom="s800" desktopXs={{paddingBottom: 's900'}}>
            <HomePageSectionTitle
              variant={isMobile ? 'titleXL' : 'title4XL'}
              className="--center"
            >
              {hasHomeSavingsCounter ? (
                <HighlightedTitle source="MonthlyTravelSavings" />
              ) : (
                <FormattedMessageWrapper
                  {...brand.messages.homeValuePropsTitleMessage}
                  values={{
                    span: chunks => (
                      <HighlightedTitleAccent>{chunks}</HighlightedTitleAccent>
                    )
                  }}
                />
              )}
            </HomePageSectionTitle>
          </Box>

          <ComparisonWrapper>
            {valueProps.map((item, index) => (
              <div key={index}>
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  paddingBottom="s500"
                >
                  <Image
                    src={item.iconUrl}
                    alt={item.iconName}
                    width={64}
                    height={64}
                  />
                </Box>
                <ComparisonItemTitle variant="titleL" align="center">
                  {formatMessage(item.title)}
                </ComparisonItemTitle>
                <Text
                  variant="bodyM"
                  align="center"
                  colorPath="content.neutral.secondary"
                >
                  {formatMessage(item.subtitle)}
                </Text>
              </div>
            ))}
          </ComparisonWrapper>
        </Box>
        {hasGetTheApp && (
          <GetAppWrapper>
            <Text
              variant={isMobile ? 'titleL' : 'title2XL'}
              align={isMobile ? 'center' : 'start'}
            >
              <FormattedMessageWrapper
                id="homePage.accessBestDealApp"
                defaultMessage="Access our best deals on our app"
              />
            </Text>

            <GetTheAppButtons
              placement={Placement.HomePageSavingsSection}
              isButtonVariant={isMobile}
              redirectLanguage={languageCode}
            />
          </GetAppWrapper>
        )}
      </SectionWrapper>
    </PageWidthContainer>
  )
}
